/**
 * One stock count as posted: the count sheet itself (books, counted,
 * difference) and, for whoever may see costs, what it was worth and the
 * journal behind it. Cancelling takes the moves out and reverses the
 * journal; the number stays spent.
 */
import { useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Box, Button, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { cancelAdjustment, fetchAdjustment } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P } from '../api/types'
import { useAuth } from '../store/AuthContext'
import ScreenTitle from '../components/ScreenTitle'
import VoucherDrawer from '../reports/VoucherDrawer'
import { Figure, FigureGrid } from '../reports/SummaryFigures'
import { useBi } from '../components/Bi'
import { gregorian, toHijri } from '../reports/hijri'
import { c, fmtMoney } from '../theme'
import { CancelDialog, Field, packsOf } from './bits'

export default function CountView() {
  const { id } = useParams()
  const { t, i18n } = useTranslation()
  const bi = useBi()
  const nav = useNavigate()
  const loc = useLocation()
  const qc = useQueryClient()
  const { can } = useAuth()
  const ar = i18n.language === 'ar'
  const q = useQuery({ queryKey: ['inv-adjustment', Number(id)],
                       queryFn: () => fetchAdjustment(Number(id)) })
  const [cancelOpen, setCancelOpen] = useState(false)
  const [voucher, setVoucher] = useState<number | null>(null)
  const saved = (loc.state as { saved?: boolean } | null)?.saved

  if (q.isLoading) return <Paper variant="outlined" sx={{ p: 3 }}><Skeleton /><Skeleton /></Paper>
  if (q.isError || !q.data) return <Alert severity="warning">{errorMessage(q.error)}</Alert>
  const x = q.data
  const live = x.status === 'POSTED'
  const signed = (v: string) => (Number(v) > 0 ? `+${v}` : Number(v) < 0 ? `−${v.slice(1)}` : v)

  return (
    <Stack spacing={2}>
      {saved && <Alert severity="success">{t('stock.countSaved', { doc: x.doc_no })}</Alert>}
      <Paper variant="outlined">
        <ScreenTitle titleKey="stock.count"
          sub={<>
            <span className="doc-no">{x.doc_no}</span> · {gregorian(x.doc_date, i18n.language)}
            {' · '}{toHijri(x.doc_date, i18n.language)}
          </>}
          right={
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {!live && <Chip color="error" variant="outlined" label={t('inv.cancelled')} />}
              {x.voucher_id && can(P.reportFinancial) && (
                <Button variant="outlined" onClick={() => setVoucher(x.voucher_id!)}>
                  {t('inv.viewVoucher')}</Button>
              )}
              {live && can(P.inventoryAdjust) && can(P.reverse) && (
                <Button color="error" variant="outlined" onClick={() => setCancelOpen(true)}>
                  {t('inv.cancel')}</Button>
              )}
              {can(P.inventoryAdjust) && (
                <Button variant="outlined" onClick={() => nav('/inventory/counts/new')}>
                  {t('stock.newCount')}</Button>
              )}
            </Stack>
          } />

        <Box sx={{ px: 2.5, pt: 1, pb: 2, display: 'grid', gap: 2,
                   gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
          <Field label={t('inv.store')} value={x.store_name} />
          <Field label={t('inv.reason')} value={t(`stock.reason${x.reason}`)} />
          <Field label={t('stock.enteredBy')} value={x.entered_by ?? '—'} />
          {x.notes && <Field label={t('inv.notes')} value={x.notes} />}
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <table className="rpt" style={{ minWidth: 680 }}>
            <thead><tr>
              <th style={{ width: 32 }}>#</th>
              <th>{t('inv.item')}</th>
              <th className="num" style={{ width: 160 }}>{t('stock.books')}</th>
              <th className="num" style={{ width: 160 }}>{t('stock.counted')}</th>
              <th className="num" style={{ width: 120 }}>{t('inv.difference')}</th>
              {x.value !== undefined && <th className="num" style={{ width: 110 }}>{t('inv.value')}</th>}
            </tr></thead>
            <tbody>
              {x.lines.map((l) => {
                const d = Number(l.diff_qty)
                return (
                  <tr key={l.id}>
                    <td className="num" style={{ color: 'var(--ink-muted)' }}>{l.line_no}</td>
                    <td dir="auto"><span className="doc-no">{l.item_code}</span> · {(ar && l.item_name_ar) || l.item_name}</td>
                    <td className="num" style={{ color: 'var(--ink-muted)' }}>{packsOf(l.book_qty, l)}</td>
                    <td className="num">{packsOf(l.counted_qty, l)}</td>
                    <td className="num" style={{ fontWeight: 600,
                      color: d < 0 ? c.dangerInk : d > 0 ? c.successInk : 'var(--ink-muted)' }}>
                      {signed(l.diff_qty)} {l.base_unit}</td>
                    {l.value !== undefined && <td className="num">{fmtMoney(l.value)}</td>}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Box>
        <Box sx={{ pt: 2 }}>
          <FigureGrid>
            <Figure label={bi('stock.short')} value={String(x.short_lines)} />
            <Figure label={bi('stock.excess')} value={String(x.excess_lines)} />
            {x.value !== undefined && (
              <Figure label={bi('stock.netValue')} value={fmtMoney(x.value)} strong
                tone={Number(x.value) < 0 ? 'danger' : undefined} />
            )}
          </FigureGrid>
          <Typography sx={{ px: 2.5, pb: 2, fontSize: 12.5, color: 'var(--ink-muted)' }}>
            {t('stock.countNote')}</Typography>
        </Box>
      </Paper>

      <CancelDialog open={cancelOpen} title={t('inv.cancelTitle', { doc: x.doc_no })}
        body={t('stock.cancelCountBody')} onClose={() => setCancelOpen(false)}
        run={(reason) => cancelAdjustment(x.id, reason)}
        onDone={() => {
          setCancelOpen(false)
          void qc.invalidateQueries({ queryKey: ['inv-adjustment'] })
          void qc.invalidateQueries({ queryKey: ['inv-adjustments'] })
          void qc.invalidateQueries({ queryKey: ['inv-items'] })
        }} />
      <VoucherDrawer voucherId={voucher} onClose={() => setVoucher(null)} />
    </Stack>
  )
}
